import {
  IonContent,
  IonPage,
  useIonViewDidEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import React, { useCallback, useEffect, useState } from 'react';
import useEnhancedAuth from '../EnhancedAuth';
import './Default.css';
import {
  apiGetSecurityQuestion,
  apiSaveSecurityQuestion,
} from '../services/api';
import AppPageHeader from '../components/AppPageHeader';
import { returnToMain } from '../utils/nav';
import { useTranslation } from 'react-i18next';
import {
  getAppError,
  getAppSuccess,
  getUserMessageOrValidationError,
} from '../utils/messages';
import {
  AccountSecurityQuestion,
  SecurityQuestion,
} from '../services/types/apitypes';
import AccountQuestionsAuth from '../usecases/account/AccountQuestionsAuth';
import { strHasMinLength } from '../utils/strings';
import { cfgSecurityAnswerMinLength, cfgSecurityQuestionLength } from '../utils/config';
import AccountQuestionsForm from '../usecases/account/AccountQuestionsForm';
import SecurityQuestions from '../usecases/shared/SecurityQuestions';
import { useHistory } from 'react-router';
import { LoadingController } from '../utils/LoadingController';
import { useToaster } from '../hooks/useToaster';

const AccountQuestionsPage: React.FC = () => {
  const auth = useEnhancedAuth();
  const [t] = useTranslation('global');
  const router = useHistory();
  const [present] = useToaster();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [block, setBlock] = useState<string>('auth');

  const [securityQuestions, setSecurityQuestions] = useState<
    AccountSecurityQuestion[]
  >([]);
  const [selectedSecurityQuestion, setSelectedSecurityQuestion] = useState<
    AccountSecurityQuestion | undefined
  >(undefined);
  const [securityAnswer, setSecurityAnswer] = useState<string>('');

  const [newSecurityQuestions, setNewSecurityQuestions] = useState<
    AccountSecurityQuestion[]
  >([]);
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);

  const [isPageActive, setIsPageActive] = useState(false);
  useIonViewDidEnter(() => {
    setIsPageActive(true);
  });
  useIonViewWillLeave(() => {
    setIsPageActive(false);
  });

  const loadDataFromApi = async () => {
    LoadingController.show();
    await apiGetSecurityQuestion(auth)
      .then((response) => {
        setSecurityQuestions(response.data);
      })
      .catch((e) => {
        console.log('Error occurred while retrieving data', e);
      })
      .finally(() => {
        LoadingController.hide();
      });
  };

  useEffect(() => {
    loadDataFromApi().then(() => console.log('Load account complete'));
  }, [auth.isLoading]);

  const getAuthValidationError = () => {
    if (!strHasMinLength(securityAnswer, cfgSecurityAnswerMinLength())) {
      return t('questions.erroranswer');
    }
    return false;
  };

  const getSecurityValidationError = () => {
    if (newSecurityQuestions?.filter((value) => !!value)?.length < cfgSecurityQuestionLength()) {
      return t('questions.errorquestion');
    }
    if (
      newSecurityQuestions?.some(
        ({ answer }) => !strHasMinLength(answer, cfgSecurityAnswerMinLength()),
      )
    ) {
      return t('questions.erroranswer');
    }
    return false;
  };

  const saveChanges = async () => {
    if (isSubmitting) {
      return;
    }

    const validationError = getSecurityValidationError();
    if (validationError !== false) {
      present(getAppError(validationError));
      return;
    }

    if (newSecurityQuestions?.some((question) => !question?.answer)) {
      return;
    }

    try {
      LoadingController.show();
      setIsSubmitting(true);
      await apiSaveSecurityQuestion(newSecurityQuestions, auth);
      present(getAppSuccess(t('questions.success')));
    } catch (error) {
      console.error('Error changing security question ', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('questions.nosuccess')));
      }
    } finally {
      setBlock('form');
      setIsSubmitting(false);
      LoadingController.hide();
    }
  };

  const selectQuestion = useCallback(
    (s: SecurityQuestion) => {
      setNewSecurityQuestions((state) => {
        const newState = [...state];
        newState[currentQuestion] = {
          questionId: s.id,
          questionText: s.value,
          answer: '',
        };
        return newState;
      });
      setBlock('form');
    },
    [currentQuestion],
  );

  const setAnswers = useCallback((answer: string, position: number) => {
    setNewSecurityQuestions((state) => {
      const newState = [...state];
      newState[position] = {
        ...newState[position],
        answer: answer,
      };
      return newState;
    });
  }, []);

  const setQuestionsBlock = useCallback((question: number) => {
    setBlock('questions');
    setCurrentQuestion(question);
  }, []);

  const verifyAnswer = () => {
    const answer = selectedSecurityQuestion?.answer || '???';
    if (securityAnswer == answer) {
      setBlock('form');
    } else {
      present(getAppError(t('questions.notanswered')));
    }
  };

  return (
    <IonPage id="accountquestions-page">
      {block === 'auth' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => returnToMain(router)}
            title={t('questions.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <AccountQuestionsAuth
              securityQuestions={securityQuestions}
              securityAnswer={securityAnswer}
              setSecurityQuestion={setSelectedSecurityQuestion}
              setSecurityAnswer={setSecurityAnswer}
              isValid={getAuthValidationError() === false}
              proceed={verifyAnswer}
            ></AccountQuestionsAuth>
          </IonContent>
        </>
      )}
      {block === 'questions' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => setBlock('form')}
            title={t('questions.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <SecurityQuestions
              selectQuestion={selectQuestion}
              excludedQuestions={newSecurityQuestions
                ?.filter((q, index) => !!q && index !== currentQuestion)
                .map((q) => q?.questionId)}
            ></SecurityQuestions>
          </IonContent>
        </>
      )}
      {block === 'form' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => setBlock('auth')}
            title={t('questions.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <AccountQuestionsForm
              toQuestions={setQuestionsBlock}
              securityQuestions={newSecurityQuestions}
              setAnswers={setAnswers}
              isValid={getSecurityValidationError() === false}
              proceed={saveChanges}
            ></AccountQuestionsForm>
          </IonContent>
        </>
      )}
    </IonPage>
  );
};

export default AccountQuestionsPage;
